import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send cookies on all axios calls
axios.defaults.withCredentials = true;

// Aviso global cuando el backend bloquea una acción por falta de capacidad.
const CAP_LABELS = { delete: "eliminar", use_ia: "usar IA", download_pdf: "descargar PDF", edit: "editar" };
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const data = error?.response?.data;
    const status = error?.response?.status;
    if (status === 403 && data?.cap_denied) {
      toast.error(`No tienes permiso para ${CAP_LABELS[data.cap_denied] || "esta acción"}.`);
    }
    // Sesión única: si el backend invalida la sesión (login en otro dispositivo),
    // limpiamos y mandamos al login con un aviso claro.
    if (status === 401 && typeof data?.detail === "string" && data.detail.includes("otro dispositivo")) {
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/system-access") {
        try { localStorage.removeItem("jared_wake"); } catch { /* noop */ }
        try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
        toast.error("Tu sesión se cerró porque se inició sesión en otro dispositivo.");
        setTimeout(() => { window.location.href = "/login"; }, 1000);
      }
    }
    return Promise.reject(error);
  }
);

// Inyecta el header de planta activa (override del Super Admin) desde el arranque
const savedPlant = localStorage.getItem("lgmg_active_plant");
if (savedPlant) axios.defaults.headers.common["x-plant-id"] = savedPlant;

// Auto-logout timeouts (TIER 1)
const IDLE_MIN_ADMIN = 15;   // Gerentes / Super Admin
const IDLE_MIN_REGULAR = 60; // Resto de roles

const AuthContext = createContext(null);

// ===== MARCA POR EMPRESA (multi-tenant) =====
const DEFAULT_BRAND = "#E31E24";
const DEFAULT_BRAND_DARK = "#c01a1f";

function clampHex(h) { return Math.max(0, Math.min(255, h)); }

function darkenHex(hex, factor = 0.82) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return DEFAULT_BRAND_DARK;
  const n = parseInt(m[1], 16);
  const r = clampHex(Math.round(((n >> 16) & 255) * factor));
  const g = clampHex(Math.round(((n >> 8) & 255) * factor));
  const b = clampHex(Math.round((n & 255) * factor));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function hexToHslString(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  let r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyBrand(color, textColor, menuColor) {
  const root = document.documentElement;
  const brand = (color && /^#?[0-9a-f]{6}$/i.test(color)) ? (color.startsWith("#") ? color : `#${color}`) : DEFAULT_BRAND;
  root.style.setProperty("--brand", brand);
  root.style.setProperty("--brand-dark", darkenHex(brand));
  const hsl = hexToHslString(brand);
  if (hsl) {
    root.style.setProperty("--primary", hsl);
    root.style.setProperty("--accent", hsl);
    root.style.setProperty("--ring", hsl);
  }
  const validHex = (c) => c && /^#?[0-9a-f]{6}$/i.test(c);
  // Color de la barra de menú (sidebar). Por defecto negro corporativo.
  root.style.setProperty("--menu-bg", validHex(menuColor) ? (menuColor.startsWith("#") ? menuColor : `#${menuColor}`) : "#0a0a0a");
  // Color de texto de marca. Por defecto blanco.
  root.style.setProperty("--brand-text", validHex(textColor) ? (textColor.startsWith("#") ? textColor : `#${textColor}`) : "#ffffff");
}

// Overlay a pantalla completa inyectado de forma SÍNCRONA en el DOM antes de
// recargar al cambiar de empresa. Evita el parpadeo donde se alcanzan a ver
// datos de la planta anterior mientras el navegador rehidrata la app.
function showPlantSwitchOverlay() {
  if (document.getElementById("plant-switch-overlay")) return;
  const brand = getComputedStyle(document.documentElement).getPropertyValue("--brand").trim() || DEFAULT_BRAND;
  const ov = document.createElement("div");
  ov.id = "plant-switch-overlay";
  ov.setAttribute("data-testid", "plant-switch-overlay");
  ov.style.cssText =
    "position:fixed;inset:0;z-index:99999;display:flex;flex-direction:column;" +
    "align-items:center;justify-content:center;gap:18px;background:#0a0a0a;" +
    "color:#fff;font-family:system-ui,-apple-system,sans-serif;";
  ov.innerHTML =
    '<div style="width:46px;height:46px;border:4px solid rgba(255,255,255,.2);' +
    'border-top-color:' + brand + ';border-radius:50%;animation:psSpin .7s linear infinite;"></div>' +
    '<div style="font-size:14px;letter-spacing:.04em;color:#cbd5e1;">Cambiando de empresa…</div>' +
    '<style>@keyframes psSpin{to{transform:rotate(360deg)}}</style>';
  document.body.appendChild(ov);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminPortal, setAdminPortal] = useState(() => localStorage.getItem("lgmg_portal") === "admin");
  const [plants, setPlants] = useState([]);
  const [activePlantId, setActivePlantId] = useState(() => localStorage.getItem("lgmg_active_plant") || null);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore logout errors */ }
    localStorage.removeItem("lgmg_portal");
    localStorage.removeItem("lgmg_active_plant");
    delete axios.defaults.headers.common["x-plant-id"];
    setActivePlantId(null);
    setPlants([]);
    setAdminPortal(false);
    setUser(false);
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
  }, []);

  // ===== PLANTAS (multi-tenant) =====
  // Carga las plantas del Super Admin e inicializa la planta activa.
  useEffect(() => {
    // `user` es null mientras /auth/me resuelve. NO tocar el override de planta
    // en ese estado: hacerlo borraría la planta activa guardada en cada recarga
    // (rompía el cambio de empresa del Super Admin, que recarga la página).
    if (user === null) return undefined;
    if (!user || !user.id || !user.is_super) {
      // Usuario normal o sin sesión confirmada: ruteo por su planta vía JWT, sin override.
      delete axios.defaults.headers.common["x-plant-id"];
      localStorage.removeItem("lgmg_active_plant");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/plants`);
        if (cancelled) return;
        setPlants(data || []);
        const stored = localStorage.getItem("lgmg_active_plant");
        const valid = stored && (data || []).some(p => p.id === stored);
        const initial = valid ? stored : (user.active_plant_id || (data && data[0]?.id) || null);
        if (initial) {
          setActivePlantId(initial);
          axios.defaults.headers.common["x-plant-id"] = initial;
          localStorage.setItem("lgmg_active_plant", initial);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const switchPlant = useCallback((plantId) => {
    if (!plantId) return;
    localStorage.setItem("lgmg_active_plant", plantId);
    axios.defaults.headers.common["x-plant-id"] = plantId;
    setActivePlantId(plantId);
    // Cubre la pantalla ANTES de recargar para que no se vean datos de la
    // empresa anterior mientras el navegador rehidrata la app con la nueva.
    showPlantSwitchOverlay();
    // Doble rAF: garantiza que el overlay se pinte antes de que el navegador
    // congele el frame actual al iniciar la recarga.
    requestAnimationFrame(() => requestAnimationFrame(() => window.location.reload()));
  }, []);

  // ===== MARCA: aplica color de la empresa activa (azul PRYSER, etc.) =====
  const rawLogo = (user && user.active_plant_logo) || null;
  const brandLogo = rawLogo
    ? ((rawLogo.startsWith("http") || rawLogo.startsWith("data:")) ? rawLogo : `${process.env.REACT_APP_BACKEND_URL}${rawLogo}`)
    : null;
  const brandColor = (user && user.active_plant_color) || "#E31E24";
  const companyName = (user && user.active_plant_name) || "LGMG";
  useEffect(() => {
    applyBrand(user && user.active_plant_color, user && user.active_plant_text_color, user && user.active_plant_menu_color);
  }, [user]);

  // ===== PWA: manifest e ícono de instalación según empresa activa =====
  useEffect(() => {
    const pid = activePlantId || (user && user.active_plant_id) || null;
    const base = process.env.REACT_APP_BACKEND_URL;
    const q = pid ? `?plant=${encodeURIComponent(pid)}` : "";
    const manifest = document.querySelector('link[rel="manifest"]');
    if (manifest) manifest.setAttribute("href", `${base}/api/pwa/manifest.webmanifest${q}`);
    const apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (apple) apple.setAttribute("href", `${base}/api/pwa/icon-192.png${q}`);
    const tc = document.querySelector('meta[name="theme-color"]');
    if (tc) tc.setAttribute("content", (user && user.active_plant_color) || "#E31E24");
  }, [activePlantId, user]);

  // ===== AUTO-LOGOUT BY INACTIVITY (TIER 1) =====
  useEffect(() => {
    if (!user || !user.id) return undefined;
    const isAdmin = user.is_super || user.role === "gerente";
    const minutes = isAdmin ? IDLE_MIN_ADMIN : IDLE_MIN_REGULAR;
    const ms = minutes * 60 * 1000;

    const reset = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        // eslint-disable-next-line no-alert
        alert(`Sesión cerrada por inactividad (${minutes} min). Vuelve a iniciar sesión.`);
        logout();
      }, ms);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, logout]);

  const login = async (email, password, portal = "operational") => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    const isAdmin = portal === "admin";
    localStorage.setItem("lgmg_portal", isAdmin ? "admin" : "operational");
    setAdminPortal(isAdmin);
    setUser(data);
    return data;
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch { setUser(false); }
  };

  return (
    <AuthContext.Provider value={{ user, loading, adminPortal, login, logout, refreshUser, setUser,
      plants, activePlantId, switchPlant, brandLogo, brandColor, companyName,
      can: (cap) => !!(user && user.capabilities && user.capabilities[cap]) }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiError(detail) {
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Devuelve un mensaje claro según el tipo de fallo (red/servidor vs credenciales).
export function describeAuthError(e) {
  // Sin respuesta del servidor = problema de red/CORS/backend caído.
  if (e && !e.response) {
    return "No se pudo conectar con el servidor. Verifica tu conexión o que la URL del backend esté bien configurada.";
  }
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (status === 401) return formatApiError(detail) || "Correo o contraseña incorrectos.";
  if (status >= 500) return "Error del servidor. Intenta de nuevo en unos momentos.";
  return formatApiError(detail) || "No se pudo iniciar sesión.";
}
